import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { generateUID } from '@aytm/lib/common/uid'
import mapMessages from '../../common/mapMessages'
import { ChatID } from '../../types'
import ChatError from '../../common/ChatError'
import { DEFAULT_MAX_TEXT_LENGTH, DEFAULT_MIN_TEXT_LENGTH, SKIPPER_AUTHOR_ID } from '../../const'

import { API_INIT_ENDPOINT, DEFAULT_POLL_MS, IDLE_POLL_MS } from './const'
import { InitParams, AssistantInitResponse, SharedAssistantState } from './types'

export default async function init<T extends SharedAssistantState>({
  assistantState,
  update,
  newChat,
  domain,
  awaitContext,
  debugName = 'ChatAssistant'
}: InitParams<T>): Promise<ChatID | ChatError> {
  if ('development' === process.env.NODE_ENV) {
    // eslint-disable-next-line no-console
    console.debug(`${debugName}: init`)
  }

  assistantState.ready = false
  assistantState.inputReady = false
  assistantState.chatID = null
  assistantState.userName = null
  assistantState.connectionStatus = 'ok'
  update()

  try {
    const context = await awaitContext({ assistantState })

    const initParams = {
      domain,
      context,
      ...(newChat && { new: true })
    }
    const initResponse = await railsPostFetch(API_INIT_ENDPOINT.URL, {
      method: API_INIT_ENDPOINT.method,
      body: JSON.stringify(initParams)
    })
    if (200 !== initResponse.status) {
      throw new ChatError('Failed to initialize chat')
    }
    const response = (await initResponse.json()) as AssistantInitResponse
    if (!response || !response.chatID || !response.userName) {
      throw new ChatError('Invalid initialization response')
    }
    assistantState.chatID = response.chatID
    assistantState.userName = response.userName
    assistantState.domain = domain
    assistantState.limits = {
      minText: response.limits?.minText ?? DEFAULT_MIN_TEXT_LENGTH,
      maxText: response.limits?.maxText ?? DEFAULT_MAX_TEXT_LENGTH
    }
    assistantState.messages = mapMessages(response.messages || [], [])
    assistantState.inputReady = response.inputReady ?? true
    assistantState.ready = true

    if (null != response.closed) {
      assistantState.closed = response.closed
    }
    if (null != response.rated) {
      assistantState.rated = response.rated
    }

    // start polling service
    if (assistantState.pollingService) {
      assistantState.pollingService.interval =
        null == response.pollMs ? (assistantState.inputReady ? DEFAULT_POLL_MS : IDLE_POLL_MS) : response.pollMs
    }

    update()
  } catch (error) {
    if ('development' === process.env.NODE_ENV) {
      // eslint-disable-next-line no-console
      console.error(`${debugName}: init error`, error, (error as ChatError)?.payload)
    }
    // add init error message
    const initErrorMessage = {
      error: true,
      id: generateUID(),
      timestamp: Date.now(),
      text: 'Alas, we cannot set sail - the winds have abandoned us.\n\nLet us [Try again](#init_chat) when they return.',
      author: SKIPPER_AUTHOR_ID
    }
    assistantState.messages = [initErrorMessage]
    assistantState.ready = true
    assistantState.inputReady = false
    update()

    return error instanceof ChatError ? error : new ChatError(error instanceof Error ? error.message : 'Unknown error')
  }

  return assistantState.chatID || ''
}
